import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment, HttpHeadersService } from '@perval/shared';
import { firstValueFrom, map } from 'rxjs';
import { PropertyType } from '../../shared/models/property-type';
import { base64ToFile } from '../../shared/utils/image.utils';
import { Report } from '../models/report.model';
import { ReportHttp } from '../models/report-http.dto';
import { fromReportHttp } from './mappers/rapport.mapper';
import { AddressGeoInfo } from '../models/address-geo-info.dto';
import { ReportDomainService } from './report-domain.service';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  private readonly mainUrl = `${environment.baseApiUrl}/perval-reports/reports`;

  constructor(
    private readonly http: HttpClient,
    private readonly httpHeadersServiceService: HttpHeadersService,
    private readonly reportDomainService: ReportDomainService,
  ) {}

  public async getReportById(id: string): Promise<Report> {
    return await firstValueFrom(
      this.http
        .get<ReportHttp>(`${this.mainUrl}/${id}`, {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        })
        .pipe(map(fromReportHttp)),
    );
  }

  public async updateAppraisal(
    reportId: string,
    appraisalBasicInfo: Report.Appraisal.BasicInfo,
  ): Promise<void> {
    await firstValueFrom(
      this.http.put<ReportHttp>(
        `${this.mainUrl}/${reportId}/section/appraisal`,
        {
          lead: appraisalBasicInfo.lead,
          comment: appraisalBasicInfo.comment,
          chosen_value: appraisalBasicInfo.chosenValue,
          chosen_high_value: appraisalBasicInfo.chosenHighValue,
          chosen_low_value: appraisalBasicInfo.chosenLowValue,
          conclusion: appraisalBasicInfo.conclusion,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  public async enableAppraisal(
    reportId: string,
    isEnabled: boolean,
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.mainUrl}/${reportId}/section/appraisal`,
        {
          is_enabled: isEnabled,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  public async updateReferences(
    reportId: string,
    reportType: string,
    references: Report.References,
  ): Promise<void> {
    await firstValueFrom(
      this.http.put<ReportHttp>(
        `${this.mainUrl}/${reportId}/section/references`,
        {
          is_display_mode_detailled: references.isDisplayModeDetailled,
          is_confidential: references.isConfidential,
          is_selection:
            reportType === 'automatic_evaluation'
              ? true
              : references.isSelection,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  public async enableReferences(
    reportId: string,
    isEnabled: boolean,
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.mainUrl}/${reportId}/section/references`,
        {
          is_enabled: isEnabled,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  public async updateCustomer(
    reportId: string,
    customer: Report.Customer,
  ): Promise<void> {
    await firstValueFrom(
      this.http.put<ReportHttp>(
        `${this.mainUrl}/${reportId}/section/customer`,
        {
          firstname: customer.firstName,
          lastname: customer.lastName,
          address: customer.address,
          postal_code: customer.postalCode,
          city: customer.city,
          phone: customer.phone,
          email: customer.email,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  public async enableCustomer(
    reportId: string,
    isEnabled: boolean,
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.mainUrl}/${reportId}/section/customer`,
        {
          is_enabled: isEnabled,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  public async updateAddress(
    reportId: string,
    address: AddressGeoInfo,
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch(`${this.mainUrl}/${reportId}/address`, address, {
        headers:
          await this.httpHeadersServiceService.getHeadersWithPervalContext(),
      }),
    );
  }

  public async updateExpert(
    reportId: string,
    expert: Report.Expert,
  ): Promise<void> {
    const formData = new FormData();

    formData.append('firstname', expert.firstName);
    formData.append('lastname', expert.lastName);
    formData.append('experiences', expert.experiences);
    formData.append('role', expert.role);

    if (expert.phone) {
      formData.append('phone', expert.phone);
    }
    if (expert.email) {
      formData.append('email', expert.email);
    }

    if (expert.photo) {
      const photoFile = base64ToFile(
        expert.photo,
        `${expert.firstName}_${expert.lastName}_photo.png`,
      );

      formData.append('photo', photoFile);
    }

    await firstValueFrom(
      this.http.put<ReportHttp>(
        `${this.mainUrl}/${reportId}/section/expert`,
        formData,
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  public async enableExpert(
    reportId: string,
    isEnabled: boolean,
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.mainUrl}/${reportId}/section/expert`,
        {
          is_enabled: isEnabled,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  public async updateCharacteristics(
    reportId: string,
    propertyType: PropertyType,
    characteristics: Report.Characteristics,
  ): Promise<void> {
    await firstValueFrom(
      this.http.put<ReportHttp>(
        `${this.mainUrl}/${reportId}/section/characteristics`,
        {
          ...this.buildCharacteristicsPayload(propertyType, characteristics),
          construction_year: characteristics.constructionYear,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  public async enableCharacteristics(
    reportId: string,
    isEnabled: boolean,
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.mainUrl}/${reportId}/section/characteristics`,
        {
          is_enabled: isEnabled,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  public async updatePerks(
    reportId: string,
    perks: Report.Perk,
  ): Promise<void> {
    await firstValueFrom(
      this.http.put<ReportHttp>(
        `${this.mainUrl}/${reportId}/section/perks`,
        {
          items: perks.items,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  public async enablePerks(
    reportId: string,
    isEnabled: boolean,
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.mainUrl}/${reportId}/section/perks`,
        {
          is_enabled: isEnabled,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  public async updateDrawbacks(
    reportId: string,
    drawbacks: Report.Drawbacks,
  ): Promise<void> {
    await firstValueFrom(
      this.http.put<ReportHttp>(
        `${this.mainUrl}/${reportId}/section/drawbacks`,
        {
          items: drawbacks.items,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  public async enableDrawbacks(
    reportId: string,
    isEnabled: boolean,
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.mainUrl}/${reportId}/section/drawbacks`,
        {
          is_enabled: isEnabled,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  public async updateOrganisme(
    reportId: string,
    organisme: Report.Organisme,
  ): Promise<void> {
    const formData = this.buildOrganizationFormData(organisme);

    await firstValueFrom(
      this.http.put<ReportHttp>(
        `${this.mainUrl}/${reportId}/section/organization`,
        formData,
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  async enableOrganisme(reportId: string, isEnabled: boolean): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.mainUrl}/${reportId}/section/organization`,
        {
          is_enabled: isEnabled,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  async enableNotes(reportId: string, isEnabled: boolean): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.mainUrl}/${reportId}/section/notes`,
        {
          is_enabled: isEnabled,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  public async updateNotes(
    reportId: string,
    notes: Report.Notes,
  ): Promise<void> {
    await firstValueFrom(
      this.http.put<ReportHttp>(
        `${this.mainUrl}/${reportId}/section/notes`,
        notes,
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  async enableDefinitions(reportId: string, isEnabled: boolean): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.mainUrl}/${reportId}/section/definitions`,
        {
          is_enabled: isEnabled,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  public async updateDefinitions(
    reportId: string,
    definitions: Report.Definitions,
  ): Promise<void> {
    await firstValueFrom(
      this.http.put<ReportHttp>(
        `${this.mainUrl}/${reportId}/section/definitions`,
        definitions,
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  async updateProperty(
    reportId: string,
    property: Report.Property,
  ): Promise<void> {
    await firstValueFrom(
      this.http.put<ReportHttp>(
        `${this.mainUrl}/${reportId}/section/property`,
        {
          description: property.description,
          surroundings_description: property.surroundingsDescription,
          viewing_date: property.viewingDate,
          occupation: property.occupation,
          condition: property.condition,
          comments: property.comments,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  async enableProperty(reportId: string, isEnabled: boolean): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.mainUrl}/${reportId}/section/property`,
        {
          is_enabled: isEnabled,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  async generateReport(reportId: string): Promise<void> {
    const fibonacciSequence = [3, 5]; // Initial sequence

    while (fibonacciSequence[fibonacciSequence.length - 1] < 34) {
      const length = fibonacciSequence.length;
      fibonacciSequence.push(
        fibonacciSequence[length - 1] + fibonacciSequence[length - 2],
      );
    }

    const checkReport = async (): Promise<boolean> => {
      try {
        const response = await firstValueFrom(
          this.http.get(`${this.mainUrl}/${reportId}/file`, {
            headers:
              await this.httpHeadersServiceService.getHeadersWithPervalContext(),
            responseType: 'blob',
            observe: 'response',
          }),
        );

        if (response.status === 200) {
          const blob = new Blob([response.body!], {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          });

          const contentDisposition = response.headers.get(
            'Content-Disposition',
          );
          const filename = this.reportDomainService.createWordName(
            contentDisposition,
            reportId,
          );

          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(url);

          console.info('Report downloaded successfully.');
          return true;
        } else if (response.status === 202 || response.status === 429) {
          console.info(
            `Report not ready yet. Retrying... Status: ${response.status}`,
          );
          return false;
        } else {
          console.error(`Unexpected status code: ${response.status}`);
          throw new Error(`Unexpected status code: ${response.status}`);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error.status === 429) {
          console.error(
            `Too many requests. Retrying... Status: ${error.status}`,
          );
          return false;
        }
        console.error('Error during report generation:', error);
        throw error;
      }
    };

    const retryWithFibonacciDelay = async (): Promise<void> => {
      for (const delay of fibonacciSequence) {
        const isReady = await checkReport();
        if (isReady) {
          return;
        }
        console.info(`Waiting ${delay} seconds before retrying...`);
        await new Promise((resolve) => setTimeout(resolve, delay * 1000));
      }
      throw new Error(
        'Report generation failed after retries with Fibonacci delays.',
      );
    };

    await retryWithFibonacciDelay();
  }

  async updatePhotographies(
    reportId: string,
    photographs: Report.Photographs,
  ): Promise<void> {
    if (
      !photographs ||
      !photographs.photographs ||
      !Array.isArray(photographs.photographs)
    ) {
      return;
    }

    const formData = this.buildPhotographFormData(photographs);

    await firstValueFrom(
      this.http.put(
        `${this.mainUrl}/${reportId}/section/photographs`,
        formData,
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  async enablePhotographies(
    reportId: string,
    isEnabled: boolean,
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.mainUrl}/${reportId}/section/photographs`,
        {
          is_enabled: isEnabled,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  async updateCase(
    reportId: string,
    detailsDossier: Report.Case,
  ): Promise<void> {
    await firstValueFrom(
      this.http.put<ReportHttp>(
        `${this.mainUrl}/${reportId}/section/case`,
        {
          reference: detailsDossier.reference,
          date: detailsDossier.date,
          address: detailsDossier.address,
          connection_to_the_property: detailsDossier.connectionToTheProperty,
          purpose: detailsDossier.purpose,
          comments: detailsDossier.comments,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  async enableCase(reportId: string, isEnabled: boolean): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.mainUrl}/${reportId}/section/case`,
        {
          is_enabled: isEnabled,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  async enableStatistics(reportId: string, isEnabled: boolean): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.mainUrl}/${reportId}/section/statistics`,
        {
          is_enabled: isEnabled,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  async enableTendencies(reportId: string, isEnabled: boolean): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.mainUrl}/${reportId}/section/tendencies`,
        {
          is_enabled: isEnabled,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  async saveTendencies(
    reportId: string,
    tendencies: Report.Tendencies,
  ): Promise<void> {
    await firstValueFrom(
      this.http.put(
        `${this.mainUrl}/${reportId}/section/tendencies`,
        {
          market_study: tendencies.marketStudy,
          market_study_scope: tendencies.marketStudyScope,
          insee_graph: tendencies.inseeGraph,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  async getReportLite(
    type: string,
    historyId: string | null,
    evaluationId: string | null,
  ): Promise<string> {
    // FIXME : Les types de service passés en paramètre ne sont pas les mêmes remontés par le historyService.
    // Une correspondance est nécessaire.
    let typeForReport!: string;

    switch (type) {
      case 'LRV':
        typeForReport = 'references';
        break;
      case 'Eval':
        typeForReport = 'manual_evaluation';
        break;
      case 'EvAu':
        typeForReport = 'automatic_evaluation';
        break;
    }

    const params: { [key: string]: string } = { type: typeForReport };

    if (historyId) {
      params['history_legacy_id'] = historyId;
    }

    if (evaluationId) {
      params['evaluation_legacy_id'] = evaluationId;
    }

    return await firstValueFrom(
      // Fixme : any
      this.http
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .get<any>(`${this.mainUrl}`, {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
          params,
        })
        .pipe(map((response) => response.items[0].id)),
    );
  }

  async resetReport(reportId: string): Promise<void> {
    await firstValueFrom(
      this.http.put(
        `${this.mainUrl}/${reportId}/reset`,
        {},
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  async enableCadastre(reportId: string, isEnabled: boolean): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.mainUrl}/${reportId}/section/cadastre`,
        {
          is_enabled: isEnabled,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  async updateCadastre(
    reportId: string,
    cadastre: Report.Cadastre,
  ): Promise<void> {
    await firstValueFrom(
      this.http.put<ReportHttp>(
        `${this.mainUrl}/${reportId}/section/cadastre`,
        {
          plot_prefixe: cadastre.plotPrefixe,
          plot_section: cadastre.plotSection,
          plot_number: cadastre.plotNumber,
          comment: cadastre.comment,
        },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }
  private buildCharacteristicsPayload(
    propertyType: PropertyType,
    characteristics: Report.Characteristics,
  ): object | undefined {
    if (propertyType.code === 'AGR' && characteristics.bienAgricole) {
      return {
        crop_type: characteristics.bienAgricole.cropType,
        farm_buildings_area_m2:
          characteristics.bienAgricole?.farmBuildingsAreaM2,
        has_farm_buildings: characteristics.bienAgricole?.hasFarmBuildings,
        has_residential_building:
          characteristics.bienAgricole?.hasResidentialBuilding,
        land_area_ha: characteristics.bienAgricole?.landAreaHa,
        residential_buildings_area_m2:
          characteristics.bienAgricole?.residentialBuildingsAreaM2,
      };
    }

    if (propertyType.code === 'APP' && characteristics.appartement) {
      return {
        energy_performance: characteristics.appartement.energyPerformance,
        exposure: characteristics.appartement.exposure,
        floor: characteristics.appartement.floor,
        ghg_emissions: characteristics.appartement.ghgEmissions,
        heating: characteristics.appartement.heating,
        living_area_m2: characteristics.appartement.livingAreaM2,
        living_room_area_m2: characteristics.appartement.livingRoomAreaM2,
        number_of_bathrooms: characteristics.appartement.numberOfBathrooms,
        number_of_bedrooms: characteristics.appartement.numberOfBedrooms,
        number_of_rooms: characteristics.appartement.numberOfRooms,
      };
    }

    if (propertyType.code === 'GAR' && characteristics.garage) {
      return {
        floor: characteristics.garage.floor,
        garage_type: characteristics.garage.garageType,
        number_of_parking_slots: characteristics.garage.numberOfParkingSlots,
      };
    }

    if (propertyType.code === 'IMM' && characteristics.immeuble) {
      return {
        has_elevator: characteristics.immeuble.hasElevator,
        has_condominium: characteristics.immeuble.hasCondominium,
        number_of_units: characteristics.immeuble.numberOfUnits,
        premises_usage: characteristics.immeuble.premisesUsage,
        profitability: characteristics.immeuble.profitability,
        total_area_m2: characteristics.immeuble.totalAreaM2,
      };
    }

    if (propertyType.code === 'MAI' && characteristics.maison) {
      return {
        energy_performance: characteristics.maison.energyPerformance,
        exposure: characteristics.maison.exposure,
        level: characteristics.maison.level,
        ghg_emissions: characteristics.maison.ghgEmissions,
        heating: characteristics.maison.heating,
        land_area_m2: characteristics.maison.landAreaM2,
        living_area_m2: characteristics.maison.livingAreaM2,
        living_room_area_m2: characteristics.maison.livingRoomAreaM2,
        number_of_bathrooms: characteristics.maison.numberOfBathrooms,
        number_of_bedrooms: characteristics.maison.numberOfBedrooms,
        number_of_rooms: characteristics.maison.numberOfRooms,
      };
    }

    if (propertyType.code === 'TER' && characteristics.terrain) {
      return {
        area_m2: characteristics.terrain.areaM2,
        facade_width_m2: characteristics.terrain.facadeWidthM2,
        viability: characteristics.terrain.viability,
        plot_ratio: characteristics.terrain.plotRatio,
        accessibility: characteristics.terrain.accessibility,
      };
    }

    if (propertyType.code === 'VIG' && characteristics.bienViticole) {
      return {
        appellation: characteristics.bienViticole.appellation,
        farm_buildings_area_m2:
          characteristics.bienViticole.farmBuildingsAreaM2,
        grape_varieties: characteristics.bienViticole.grapeVarieties,
        has_farm_buildings: characteristics.bienViticole.hasFarmBuildings,
        has_residential_building:
          characteristics.bienViticole.hasResidentialBuilding,
        residential_buildings_area_m2:
          characteristics.bienViticole.residentialBuildingsAreaM2,
        vineyard_area_ha: characteristics.bienViticole.vineyardAreaHa,
      };
    }

    if (propertyType.code === 'LAC' && characteristics.localActivite) {
      return {
        area_m2: characteristics.localActivite.areaM2,
        nature: characteristics.localActivite.nature,
        number_of_parking_spaces:
          characteristics.localActivite.numberOfParkingSpaces,
        public_transport_nearby: {
          selectedOptions: characteristics.localActivite.publicTransportNearby,
        },
      };
    }

    return undefined;
  }

  private buildPhotographFormData(photographs: Report.Photographs) {
    const formData = new FormData();
    const photographDescriptions: { title: string; index: string }[] = [];

    photographs.photographs.forEach((photograph) => {
      photographDescriptions.push({
        title: photograph.title,
        index: photograph.index.toString(),
      });

      if (photograph.photo) {
        const photoFile = base64ToFile(
          photograph.photo,
          `photo_${photograph.index}.png`,
        );
        formData.append('photographs', photoFile);
      }
    });

    formData.append(
      'data',
      new Blob([JSON.stringify({ photographs: photographDescriptions })], {
        type: 'application/json',
      }),
    );

    return formData;
  }

  private buildOrganizationFormData(organisme: Report.Organisme) {
    const formData = new FormData();

    const data = {
      name: organisme.name || '',
      address_line1: organisme.addressLine1 || '',
      address_line2: organisme.addressLine2 || '',
      address_line3: organisme.addressLine3 || '',
      postal_code: organisme.postalCode || '',
      city: organisme.city || '',
      expertise: organisme.expertise || '',
      services:
        organisme.services?.map((service) => ({
          title: service.title,
          description: service.description,
        })) || [],
    };

    formData.append(
      'data',
      new Blob([JSON.stringify(data)], { type: 'application/json' }),
    );

    if (organisme.photo) {
      const photoFile = base64ToFile(
        organisme.photo,
        `${organisme.name || 'photo'}.png`,
      );
      formData.append('photo', photoFile);
    }

    if (organisme.logo) {
      const logoFile = base64ToFile(
        organisme.logo,
        `${organisme.name || 'logo'}.png`,
      );
      formData.append('logo', logoFile);
    }

    return formData;
  }
}
